import type { ReactionOption } from "@somethings-ui/web";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useMemo } from "react";

// Local shape so Sendbird's EmojiContainer types don't leak into the pure builder.
interface AttentionEmojiEntry {
  key: string;
  url: string;
}

interface AttentionEmojiCategoryLike {
  emojis: AttentionEmojiEntry[];
}

export interface AttentionEmojiOptions {
  /** Picker choices; visuals are the native Sendbird emoji PNGs keyed by `key`. */
  reactionOptions: ReactionOption[];
  /** Resolves a reaction `key` to its PNG url for rendered reaction pills. */
  emojiUrlByKey: Map<string, string>;
}

// Pure so the flatten + key/url mapping is testable without Sendbird or jsdom.
export function buildAttentionEmojiOptions(
  emojiCategories: AttentionEmojiCategoryLike[] | undefined,
): AttentionEmojiOptions {
  const entries = emojiCategories?.flatMap((category) => category.emojis) ?? [];
  const reactionOptions: ReactionOption[] = entries.map((entry) => ({
    key: entry.key,
    label: entry.key,
    node: <img src={entry.url} alt="" className="size-5 object-contain" />,
  }));
  const emojiUrlByKey = new Map(entries.map((entry) => [entry.key, entry.url]));
  return { reactionOptions, emojiUrlByKey };
}

// Reuses the emoji set the UIKit store already loaded (no fetch of our own),
// confining Sendbird types here so callers see only plain ReactionOption[] and a map.
export function useAttentionEmojis(): AttentionEmojiOptions {
  const store = useSendbirdStateContext();
  const emojiContainer = store.emojiManager?.emojiContainer;
  return useMemo(
    () => buildAttentionEmojiOptions(emojiContainer?.emojiCategories),
    [emojiContainer],
  );
}
